<script setup>
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const store = useStore();
const { t } = useI18n();

const inboxes = useMapGetter('inboxes/getInboxes');
const uiFlags = useMapGetter('inboxes/getUIFlags');

const keyInputs = reactive({});
const errors = reactive({});
const savingInboxId = ref(null);

const dualhookInboxes = computed(() =>
  (inboxes.value ?? [])
    .filter(inbox => inbox.provider_config?.source === 'dualhook')
    .sort((a, b) => a.name.localeCompare(b.name))
);

const maskedKey = key => `${key.slice(0, 8)}…${key.slice(-4)}`;

const saveKey = async inbox => {
  const apiKey = (keyInputs[inbox.id] ?? '').trim();
  if (!apiKey) return;

  savingInboxId.value = inbox.id;
  errors[inbox.id] = '';
  try {
    await store.dispatch('inboxes/updateInbox', {
      id: inbox.id,
      formData: false,
      channel: {
        provider_config: { ...inbox.provider_config, api_key: apiKey },
      },
    });
    keyInputs[inbox.id] = '';
    useAlert(t('INBOX_MGMT.DUALHOOK_KEYS.SAVE_SUCCESS'));
  } catch (error) {
    errors[inbox.id] =
      error.message || t('INBOX_MGMT.DUALHOOK_KEYS.SAVE_ERROR');
  } finally {
    savingInboxId.value = null;
  }
};
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :no-records-found="!dualhookInboxes.length"
    :no-records-message="$t('INBOX_MGMT.DUALHOOK_KEYS.EMPTY')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('INBOX_MGMT.DUALHOOK_KEYS.TITLE')"
        :description="$t('INBOX_MGMT.DUALHOOK_KEYS.DESCRIPTION')"
        :back-button-label="$t('INBOX_MGMT.DUALHOOK_KEYS.BACK_BUTTON')"
      />
    </template>
    <template #body>
      <div class="divide-y divide-n-weak border-t border-n-weak">
        <div
          v-for="inbox in dualhookInboxes"
          :key="inbox.id"
          class="flex flex-col gap-3 py-4"
        >
          <div class="flex items-start justify-between gap-4">
            <div class="flex flex-col gap-1 min-w-0">
              <span class="text-heading-3 text-n-slate-12 truncate">
                {{ inbox.name }}
              </span>
              <div
                class="flex items-center gap-2 min-w-0 text-body-main text-n-slate-11"
              >
                <span class="truncate">{{ inbox.phone_number }}</span>
                <span class="size-1 rounded-full bg-n-slate-8 flex-shrink-0" />
                <span class="truncate">
                  {{ inbox.provider_config.dualhook_connection_id }}
                </span>
              </div>
            </div>
            <span
              v-if="inbox.provider_config.api_key"
              class="flex-shrink-0 rounded-lg bg-n-teal-3 px-2 py-1 text-label-small text-n-teal-11"
            >
              {{
                $t('INBOX_MGMT.DUALHOOK_KEYS.KEY_SET', {
                  key: maskedKey(inbox.provider_config.api_key),
                })
              }}
            </span>
            <span
              v-else
              class="flex-shrink-0 rounded-lg bg-n-ruby-3 px-2 py-1 text-label-small text-n-ruby-11"
            >
              {{ $t('INBOX_MGMT.DUALHOOK_KEYS.KEY_MISSING') }}
            </span>
          </div>
          <div class="flex items-start gap-2">
            <Input
              v-model="keyInputs[inbox.id]"
              class="flex-1"
              :placeholder="$t('INBOX_MGMT.DUALHOOK_KEYS.KEY_PLACEHOLDER')"
              :message="errors[inbox.id]"
              message-type="error"
              @enter="saveKey(inbox)"
            />
            <Button
              :label="$t('INBOX_MGMT.DUALHOOK_KEYS.SAVE_BUTTON')"
              :is-loading="savingInboxId === inbox.id"
              :disabled="!keyInputs[inbox.id] || savingInboxId === inbox.id"
              @click="saveKey(inbox)"
            />
          </div>
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
